// src/hooks/useMembership.ts - Version avec envoi d'emails automatiques
import { useState } from 'react';
import { supabase, SupabaseService } from '../services/supabaseClient';

// Types pour le hook avec les nouveaux champs
interface MembershipFormData {
    firstName: string;
    lastName: string;
    phone: string;
    email?: string | undefined;
    nip?: string | undefined;
    country: string;
    province: string;
    department?: string | undefined;
    city: string;
    customCity?: string | undefined;
    profession: string;
    gender: string;
    referredBy?: string | undefined;
    selfieBase64?: string | null | undefined;
    identityDocumentBase64?: string | null | undefined;
    signatureData?: string | null | undefined;
    dateOfBirth?: string | undefined;
    placeOfBirth?: string | undefined;
}

interface CreatedMember {
    id: string;
    member_number: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    nip: string | null;
    country: string;
    province: string;
    department: string | null;
    city: string;
    profession: string;
    gender: string;
    selfie_url?: string;
    identity_document_url?: string;
    signature_data?: string | null;
    referral_code: string;
    badges: string[];
    referrals_count: number;
    created_at: string;
}

interface ReferrerInfo {
    firstName: string;
    lastName: string;
    city: string;
    province: string;
    email: string;
}

export const useMembership = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [emailStatus, setEmailStatus] = useState<{
        welcome?: { success: boolean; message: string };
        confirmation?: { success: boolean; message: string };
        referralNotification?: { success: boolean; message: string };
    }>({});

    const checkNipAvailability = async (nip: string): Promise<{ available: boolean; message?: string }> => {
        try {
            if (!nip || nip.trim() === '') {
                return { available: false, message: 'NIP requis' };
            }

            const nipPattern = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{4}-[A-Za-z0-9]{8}$/;
            if (!nipPattern.test(nip)) {
                return { available: false, message: 'Format NIP invalide' };
            }

            const { data, error } = await supabase
                .from('members')
                .select('id, first_name, last_name')
                .eq('nip', nip.trim())
                .limit(1);

            if (error) {
                console.error('Erreur vérification NIP:', error);
                return { available: false, message: 'Erreur de vérification' };
            }

            if (data && data.length > 0) {
                return {
                    available: false,
                    message: `Ce NIP est déjà utilisé par un autre membre`
                };
            }

            return { available: true, message: 'NIP disponible' };

        } catch (error) {
            console.error('Erreur checkNipAvailability:', error);
            return { available: false, message: 'Erreur de vérification' };
        }
    };

    const uploadSelfie = async (base64Data: string): Promise<string | null> => {
        try {
            const base64String = base64Data.split(',')[1];
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.jpg`;

            const { error } = await supabase.storage
                .from('member-photos')
                .upload(`selfies/${fileName}`, blob, {
                    contentType: 'image/jpeg',
                    upsert: false
                });

            if (error) {
                console.error('Erreur upload Supabase:', error);
                if (error.message.includes('not found') || error.message.includes('does not exist')) {
                    const { error: bucketError } = await supabase.storage.createBucket('member-photos', {
                        public: true,
                        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
                        fileSizeLimit: 5242880
                    });

                    if (bucketError) {
                        throw new Error('Impossible de créer le bucket de stockage');
                    }

                    const { error: retryError } = await supabase.storage
                        .from('member-photos')
                        .upload(`selfies/${fileName}`, blob, {
                            contentType: 'image/jpeg'
                        });

                    if (retryError) throw retryError;
                }
            }

            const { data: { publicUrl } } = supabase.storage
                .from('member-photos')
                .getPublicUrl(`selfies/${fileName}`);

            return publicUrl;

        } catch (error) {
            console.error('Erreur upload selfie:', error);
            return null;
        }
    };

    const uploadIdentityDocument = async (base64Data: string): Promise<string | null> => {
        try {
            const base64String = base64Data.split(',')[1];
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const fileName = `identity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.jpg`;

            const { error } = await supabase.storage
                .from('member-photos')
                .upload(`identity-docs/${fileName}`, blob, {
                    contentType: 'image/jpeg',
                    upsert: false
                });

            if (error) throw error;

            const { data: { publicUrl } } = supabase.storage
                .from('member-photos')
                .getPublicUrl(`identity-docs/${fileName}`);

            return publicUrl;

        } catch (error) {
            console.error('Erreur upload document:', error);
            return null;
        }
    };

    // Normalisation des noms pour gérer les erreurs de saisie
    const normalizeName = (name: string): string => {
        return name
            .trim() // Supprimer espaces début/fin
            .replace(/\s+/g, ' ') // Remplacer multiples espaces par un seul
            .toUpperCase() // Uniformiser la casse
            .normalize('NFD') // Décomposer les accents
            .replace(/[\u0300-\u036f]/g, ''); // Supprimer les diacritiques
    };

    const getMemberByNipAndLastName = async (nip: string, lastName: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const normalizedLastName = normalizeName(lastName);
            
            // Recherche avec nom exact normalisé
            let { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('nip', nip.trim())
                .eq('status', 'active')
                .ilike('last_name', normalizedLastName)
                .single();

            // Si pas trouvé, recherche avec similarité
            if (error && error.code === 'PGRST116') {
                const { data: searchData } = await supabase
                    .from('members')
                    .select('*')
                    .eq('nip', nip.trim())
                    .eq('status', 'active');

                if (searchData && searchData.length > 0) {
                    // Chercher une correspondance approximative
                    const member = searchData.find(m => {
                        const dbLastName = normalizeName(m.last_name);
                        const inputLastName = normalizeName(lastName);
                        
                        // Correspondance exacte après normalisation
                        if (dbLastName === inputLastName) return true;
                        
                        // Correspondance si l'un contient l'autre (noms composés)
                        if (dbLastName.includes(inputLastName) || inputLastName.includes(dbLastName)) return true;
                        
                        return false;
                    });

                    if (member) {
                        return member;
                    }
                }
                
                setError('Aucun membre trouvé avec ces informations');
                return null;
            }

            if (error) {
                setError('Informations d\'authentification incorrectes');
                return null;
            }

            return data;

        } catch (error: any) {
            setError('Erreur lors de l\'authentification');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getMemberByPhoneAndLastName = async (phone: string, lastName: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const normalizedLastName = normalizeName(lastName);
            const cleanPhone = phone.replace(/\D/g, ''); // Garder seulement les chiffres
            
            // Recherche avec nom exact normalisé
            let { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('status', 'active')
                .ilike('phone', `%${cleanPhone.slice(-8)}%`) // Recherche sur les 8 derniers chiffres
                .ilike('last_name', normalizedLastName)
                .single();

            // Si pas trouvé, recherche avec similarité
            if (error && error.code === 'PGRST116') {
                const { data: searchData } = await supabase
                    .from('members')
                    .select('*')
                    .eq('status', 'active')
                    .ilike('phone', `%${cleanPhone.slice(-8)}%`);

                if (searchData && searchData.length > 0) {
                    const member = searchData.find(m => {
                        const dbLastName = normalizeName(m.last_name);
                        const inputLastName = normalizeName(lastName);
                        
                        if (dbLastName === inputLastName) return true;
                        if (dbLastName.includes(inputLastName) || inputLastName.includes(dbLastName)) return true;
                        
                        return false;
                    });

                    if (member) {
                        return member;
                    }
                }
                
                setError('Aucun membre trouvé avec ces informations');
                return null;
            }

            if (error) {
                setError('Informations d\'authentification incorrectes');
                return null;
            }

            return data;

        } catch (error: any) {
            setError('Erreur lors de l\'authentification');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getMemberBySecureCredentials = async (lastName: string, memberNumber: string, dateOfBirth: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const normalizedLastName = normalizeName(lastName);
            
            // Recherche sécurisée avec 3 facteurs
            const { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('status', 'active')
                .eq('member_number', memberNumber.trim().toUpperCase())
                .eq('date_of_birth', dateOfBirth.trim())
                .ilike('last_name', normalizedLastName)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    setError('Aucun membre trouvé avec ces informations d\'identification');
                } else {
                    setError('Informations d\'authentification incorrectes');
                }
                return null;
            }

            return data;

        } catch (error: any) {
            setError('Erreur lors de l\'authentification sécurisée');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const validateReferralCode = async (code: string): Promise<{ valid: boolean; referrer?: ReferrerInfo }> => {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('id, first_name, last_name, city, province, email')
                .eq('referral_code', code)
                .single();

            if (error || !data) {
                return { valid: false };
            }

            return {
                valid: true,
                referrer: {
                    firstName: data.first_name,
                    lastName: data.last_name,
                    city: data.city,
                    province: data.province,
                    email: data.email // Ajout de l'email pour les notifications
                }
            };
        } catch (error) {
            return { valid: false };
        }
    };

    // ==========================================
    // FONCTION PRINCIPALE - Avec envoi d'emails
    // ==========================================
    const createMembership = async (formData: MembershipFormData): Promise<CreatedMember | null> => {
        setIsLoading(true);
        setError(null);
        setEmailStatus({});

        try {
            // 1. Validations côté client
            if (!formData.signatureData?.trim()) {
                throw new Error('La signature électronique est obligatoire');
            }
            if (!formData.firstName?.trim()) {
                throw new Error('Le prénom est obligatoire');
            }
            if (!formData.lastName?.trim()) {
                throw new Error('Le nom est obligatoire');
            }
            if (!formData.phone?.trim()) {
                throw new Error('Le téléphone est obligatoire');
            }
            if (!formData.city?.trim()) {
                throw new Error('La ville est obligatoire');
            }
            if (formData.country === 'Gabon' && !formData.province?.trim()) {
                throw new Error('La province est obligatoire pour le Gabon');
            }
            if (!formData.profession?.trim()) {
                throw new Error('La profession est obligatoire');
            }
            if (!formData.gender?.trim()) {
                throw new Error('Le sexe est obligatoire');
            }

            if (formData.email?.trim()) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    throw new Error('Adresse email invalide');
                }
            }

            // 2. Récupérer les infos du parrain si applicable
            let referrerData = null;
            if (formData.referredBy?.trim()) {
                const referrerValidation = await validateReferralCode(formData.referredBy.trim());
                if (referrerValidation.valid) {
                    referrerData = referrerValidation.referrer;
                }
            }

            // 3. Upload des fichiers en parallèle
            const [selfieUrl, identityDocUrl] = await Promise.all([
                formData.selfieBase64 ? uploadSelfie(formData.selfieBase64) : Promise.resolve(null),
                formData.identityDocumentBase64 ? uploadIdentityDocument(formData.identityDocumentBase64) : Promise.resolve(null)
            ]);

            // 4. Créer le membre en base de données
            const { data, error } = await supabase.rpc('create_member_with_params_v2', {
                first_name: formData.firstName.trim(),
                last_name: formData.lastName.trim(),
                phone: formData.phone.trim(),
                province: formData.province?.trim() || '',
                city: formData.city === 'autre' ? (formData.customCity?.trim() || '') : formData.city.trim(),
                profession: formData.profession.trim(),
                gender: formData.gender.trim(),
                email: formData.email?.trim() || null,
                nip: formData.nip?.trim() || null,
                country: formData.country?.trim() || 'Gabon',
                department: formData.department?.trim() || null,
                selfie_url: selfieUrl,
                identity_document_url: identityDocUrl,
                signature_data: formData.signatureData,
                referred_by_code: formData.referredBy?.trim() || null,
                date_of_birth: formData.dateOfBirth || null,
                place_of_birth: formData.placeOfBirth?.trim() || null
            });

            if (error) {
                console.error('Erreur création membre:', error);
                throw new Error(error.message || 'Erreur lors de la création du membre');
            }

            if (!data?.success) {
                throw new Error(data?.message || 'Erreur lors de la création du membre');
            }

            const createdMember = data.member;

            // 5. ✅ ENVOI D'EMAILS AUTOMATIQUES (ne pas bloquer si échec)
            if (createdMember.email) {
                console.log('📧 Début envoi des emails d\'adhésion pour:', createdMember.email);

                try {
                    // Envoi email de bienvenue via Edge Function
                    const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-adhesion-email', {
                        body: {
                            emailType: 'welcome',
                            memberData: {
                                member_number: createdMember.member_number,
                                first_name: createdMember.first_name,
                                last_name: createdMember.last_name,
                                email: createdMember.email,
                                phone: createdMember.phone,
                                city: createdMember.city,
                                province: createdMember.province,
                                country: createdMember.country || 'Gabon',
                                referral_code: createdMember.referral_code,
                                created_at: createdMember.created_at
                            },
                            additionalData: {
                                loginUrl: `${window.location.origin}/membre/${createdMember.member_number}`,
                                referralLink: `${window.location.origin}/adhesion?ref=${createdMember.referral_code}`,
                                supportEmail: 'support@udb.ga'
                            }
                        }
                    });

                    if (emailError) {
                        console.warn('⚠️ Erreur envoi email de bienvenue:', emailError);
                        setEmailStatus({
                            welcome: { success: false, message: emailError.message || 'Erreur envoi email' }
                        });
                    } else {
                        console.log('✅ Email de bienvenue envoyé:', emailResult);
                        setEmailStatus({
                            welcome: { success: true, message: 'Email de bienvenue envoyé' }
                        });
                    }

                    // Envoi notification au parrain si applicable
                    if (referrerData && referrerData.email) {
                        const { error: referralEmailError } = await supabase.functions.invoke('send-adhesion-email', {
                            body: {
                                emailType: 'referral_notification',
                                memberData: {
                                    member_number: createdMember.member_number,
                                    first_name: createdMember.first_name,
                                    last_name: createdMember.last_name,
                                    email: referrerData.email,
                                    city: createdMember.city,
                                    province: createdMember.province,
                                    referral_code: createdMember.referral_code,
                                    created_at: createdMember.created_at
                                },
                                additionalData: {
                                    referrerData,
                                    newMemberName: `${createdMember.first_name} ${createdMember.last_name}`,
                                    newMemberCity: createdMember.city,
                                    newMemberProvince: createdMember.province
                                }
                            }
                        });

                        if (!referralEmailError) {
                            console.log('✅ Notification parrain envoyée');
                        }
                    }

                } catch (emailError) {
                    console.warn('⚠️ Erreur envoi emails (non-bloquant):', emailError);
                    setEmailStatus({
                        welcome: { success: false, message: 'Erreur envoi email' }
                    });
                }
            } else {
                console.log('ℹ️ Pas d\'email fourni, envoi d\'emails ignoré');
                setEmailStatus({
                    welcome: { success: true, message: 'Pas d\'email fourni' }
                });
            }

            // 6. Retourner le membre créé
            return {
                id: createdMember.id,
                member_number: createdMember.member_number,
                first_name: createdMember.first_name,
                last_name: createdMember.last_name,
                email: createdMember.email,
                phone: createdMember.phone,
                nip: createdMember.nip,
                country: createdMember.country,
                province: createdMember.province,
                department: createdMember.department,
                city: createdMember.city,
                profession: createdMember.profession,
                gender: createdMember.gender,
                selfie_url: createdMember.selfie_url,
                identity_document_url: createdMember.identity_document_url,
                signature_data: createdMember.signature_data,
                referral_code: createdMember.referral_code,
                badges: createdMember.badges,
                referrals_count: createdMember.referrals_count,
                created_at: createdMember.created_at,
                firstName: createdMember.first_name,
                lastName: createdMember.last_name
            } as CreatedMember;

        } catch (err: any) {
            const errorMessage = err.message || 'Erreur lors de la création de l\'adhésion';
            setError(errorMessage);
            console.error('Erreur createMembership:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Fonction pour obtenir les statistiques globales
    const getGlobalStats = async () => {
        try {
            const { data, error } = await supabase.rpc('get_realtime_stats');

            if (error) {
                console.error('Erreur récupération stats:', error);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Erreur récupération stats:', error);
            return null;
        }
    };

    const getMemberByReferralCode = async (code: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('referral_code', code)
                .single();

            if (error || !data) {
                setError('Membre non trouvé');
                return null;
            }

            return data;
        } catch (err: any) {
            setError('Erreur lors de la récupération du membre');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getMemberByCredentials = async (firstName: string, lastName: string, memberNumber: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await SupabaseService.getMemberByCredentials(firstName, lastName, memberNumber);

            if (!result.success) {
                setError(result.error || 'Informations d\'authentification incorrectes');
                return null;
            }

            return result.member;
        } catch (error: any) {
            setError('Erreur lors de l\'authentification');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const testConnection = async (): Promise<boolean> => {
        try {
            const { error } = await supabase
                .from('members')
                .select('count')
                .limit(1);

            return !error;
        } catch (error) {
            return false;
        }
    };

    const clearError = () => {
        setError(null);
    };

    const generateShareLinks = (referralCode: string) => {
        const baseUrl = window.location.origin;
        const referralLink = `${baseUrl}/adhesion?ref=${referralCode}`;

        const facebookMessage = `🎉 Rejoignez l'Union Démocratique des Bâtisseurs !

Je vous invite à rejoindre le mouvement politique pour transformer le Gabon.

✅ Bénéficiez de mon parrainage
✅ Adhésion simplifiée  
✅ Accompagnement personnalisé

🔗 Utilisez mon code de parrainage: ${referralCode}

👉 ${referralLink}

#UDB #UnionDesBatisseurs #Gabon #Politique`;

        const whatsappMessage = `Rejoignez l'Union Démocratique des Bâtisseurs !

Salut ! Je t'invite à rejoindre l'UDB, le mouvement qui transforme le Gabon.

Avec mon code de parrainage *${referralCode}*, tu bénéficies d'un accompagnement personnalisé !

 ${referralLink}

#UDB #UnionDesBatisseurs`;

        return {
            referralLink,
            facebookMessage,
            whatsappMessage,
            whatsapp: `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(facebookMessage)}&hashtag=${encodeURIComponent('#UDB')}`,
            telegram: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(facebookMessage)}`,
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`🎉 Rejoignez l'UDB avec mon code ${referralCode}!\n\n👉 ${referralLink}\n\n#UDB #UnionDesBatisseurs #Gabon`)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}&summary=${encodeURIComponent('Rejoignez l\'Union Démocratique des Bâtisseurs')}`
        };
    };

    return {
        // Fonctions principales
        createMembership,
        validateReferralCode,
        getMemberByReferralCode,
        getGlobalStats,
        getMemberByCredentials,
        getMemberByNipAndLastName,
        getMemberByPhoneAndLastName,
        getMemberBySecureCredentials,
        checkNipAvailability,

        // Utilitaires
        testConnection,
        generateShareLinks,
        clearError,

        // États
        isLoading,
        error,
        emailStatus // ✅ NOUVEAU: Statut des emails envoyés
    };
};

export default useMembership;
